import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./PostSubmissionStepper.css";
import AuthService from "../../Services/AuthService";
import PropertiesService from "../../Services/PropertiesService";

const PostSubmissionStepper = () => {
    const [step, setStep] = useState(1);
    const [videosWatched, setVideosWatched] = useState(false);
    const [docChecked, setDocChecked] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const navigate = useNavigate();

    const steps = ["Videos", "Documents", "Verification"];

    const getUserIdFromStorage = () => {
        const userDetails = JSON.parse(localStorage.getItem("userDetails"));
        return userDetails?.civilEng_id;
    };

    const syncStepFromServer = useCallback(async () => {
        const userId = getUserIdFromStorage();
        if (!userId) return;

        try {
            const response = await PropertiesService.getCivilEngineer({ userId });
            const data = response?.data?.data;
            if (!data) return;

            // If admin has approved the engineer, go straight to dashboard.
            if (data?.status === "Approved") {
                navigate("/navbar/dashboard");
                return;
            }

            const videosApproved = data?.videosApproval === "Approved";
            const docsApproved = data?.documentationApproval === "Approved";

            setVideosWatched(videosApproved);
            setDocChecked(docsApproved);

            if (videosApproved && docsApproved) setStep(3);
            else if (!videosApproved) setStep(1);
            else setStep(2);
        } catch (e) {
            // Keep user on current step; surface error
            toast.error(e?.message || "Failed to load approval status");
        }
    }, [navigate]);

    useEffect(() => {
        syncStepFromServer();
        // Refresh step logic whenever user comes back to the tab
        const handleFocus = () => syncStepFromServer();
        window.addEventListener("focus", handleFocus);
        return () => window.removeEventListener("focus", handleFocus);
    }, [syncStepFromServer]);

    const updateCivilEngData = async (patch) => {
        const userId = getUserIdFromStorage();
        if (!userId) return false;
        setIsSaving(true);
        try {
            const res = await AuthService.updateCivilEngData({ userId, ...patch });
            if (res?.message !== "Success") {
                toast.error(
                    typeof res === "string" ? res : (res?.message || "Failed to update")
                );
                return false;
            }
            // Re-check server state so refresh behavior matches backend
            await syncStepFromServer();
            return true;
        } catch (e) {
            toast.error(e?.message || "Failed to update");
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleNext = async () => {
        if (isSaving) return;

        // Step 1: just move to step 2, no API call
        if (step === 1) {
            if (!videosWatched) return;
            setStep(2);
            return;
        }

        // Step 2: call API once and only move to step 3 on success
        if (step === 2) {
            if (!docChecked) return;
            const ok = await updateCivilEngData({
                // both approvals can be considered completed once user submits step 2
                videosApproval: "Approved",
                documentationApproval: "Approved"
            });
            if (ok) {
                setStep(3);
            }
            return;
        }
    };

    const handleBack = () => {
        if (step > 1) setStep((s) => s - 1);
    };

    return (
        <div className="post-submission-wrapper">
            <div className="stepper-card">

                <div className="stepper-header">Materials to be reffered</div>

                <div className="stepper">
                    {steps.map((_, index) => (
                        <div
                            key={index}
                            className={`step
        ${step - 1 === index ? "active" : ""}
        ${step - 1 > index ? "completed" : ""}
      `}
                        >
                            <div className="circle">{index + 1}</div>
                        </div>
                    ))}
                </div>

                {/* Step Content */}
                <div className="stepper-body">
                    {step === 1 && (
                        <div className="step-content">
                            <div className="videos-list">
                                <div className="video-item">
                                    <video controls width="320">
                                        <source src="/static/media/sample-video.mp4" type="video/mp4" />
                                    </video>
                                </div>
                                <div className="video-item">
                                    <video controls width="320">
                                        <source src="/static/media/sample-video.mp4" type="video/mp4" />
                                    </video>
                                </div>
                            </div>

                            <label className="watch-checkbox">
                                <input
                                    type="checkbox"
                                    checked={videosWatched}
                                    disabled={isSaving}
                                    onChange={(e) => setVideosWatched(e.target.checked)}
                                />
                                I have watched the videos
                            </label>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="step-content">
                            <div className="docs-list">
                                <div className="doc-item">
                                    <div className="doc-title">DoCity Guidelines.pdf</div>
                                    <div className="doc-actions">
                                        <a href="/static/media/sample-doc.pdf" target="_blank" rel="noreferrer">
                                            View
                                        </a>
                                        <a href="/static/media/sample-doc.pdf" download>
                                            Download
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <label className="doc-checkbox">
                                <input
                                    type="checkbox"
                                    checked={docChecked}
                                    disabled={isSaving}
                                    onChange={(e) => setDocChecked(e.target.checked)}
                                />
                                I have viewed and understood the documentation
                            </label>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="step-content">
                            <p>
                                Your profile will be submitted to the DoCity administration team for
                                review. Once the background verification process is completed, you will
                                be granted access to DoCity.
                                Until then, please wait for the admin's response. If you have any questions,
                                you can reach out to us: <a href="mailto:docityhelp@gmail.com">
                                    docityhelp@gmail.com
                                </a>
                            </p>

                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="stepper-footer">
                    <button
                        onClick={handleBack}
                        disabled={step === 1}
                        className="secondary"
                    >
                        Back
                    </button>

                    {step < 3 && (
                        <button
                            onClick={handleNext}
                            className="primary"
                            disabled={
                                (step === 1 && !videosWatched) ||
                                (step === 2 && !docChecked)
                            }
                        >
                            Next
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PostSubmissionStepper;
